import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig, calculateBringForwardRule } from './FinancialConfig.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const SMSFBringForwardCalculator = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('calculator');
    
    const [bringForwardData, setBringForwardData] = useState({
        totalSuperBalance: 800000,
        age: 55,
        year1Contribution: 0,
        year2Contribution: 0,
        year3Contribution: 0,
        plannedYear1: 200000,
        plannedYear2: 100000,
        plannedYear3: 60000,
        triggerYear: new Date().getFullYear()
    });

    const [results, setResults] = useState(null);

    const BRING_FORWARD = FinancialConfig.BRING_FORWARD_RULE;
    const TSB_THRESHOLD = FinancialConfig.TSB_THRESHOLDS.BRING_FORWARD_TRIGGER;
    const NON_CONC_CAP = FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL;

    const calculateBringForward = () => {
        const errors = [];
        const warnings = [];

        // Age eligibility
        if (bringForwardData.age >= 75) {
            errors.push({
                type: 'age',
                message: 'Bring-forward rule not available for members aged 75+',
                value: bringForwardData.age
            });
        }

        // TSB eligibility
        if (bringForwardData.totalSuperBalance >= FinancialConfig.CONTRIBUTION_CAPS.TOTAL_BALANCE_THRESHOLD) {
            errors.push({
                type: 'tsb',
                message: `Total Super Balance ($${bringForwardData.totalSuperBalance.toLocaleString()}) exceeds $${FinancialConfig.CONTRIBUTION_CAPS.TOTAL_BALANCE_THRESHOLD.toLocaleString()} - no non-concessional contributions allowed`,
                value: bringForwardData.totalSuperBalance
            });
        } else if (bringForwardData.totalSuperBalance >= TSB_THRESHOLD) {
            warnings.push({
                type: 'tsb',
                message: `TSB above $${TSB_THRESHOLD.toLocaleString()} - bring-forward may be limited or unavailable`,
                severity: 'warning'
            });
        }

        // Calculate contribution totals and the TSB-dependent bring-forward band.
        const year1Total = bringForwardData.year1Contribution + bringForwardData.plannedYear1;
        const year2Total = bringForwardData.year2Contribution + bringForwardData.plannedYear2;
        const year3Total = bringForwardData.year3Contribution + bringForwardData.plannedYear3;
        const threeYearTotal = year1Total + year2Total + year3Total;

        // Check if bring-forward is triggered
        const isTriggered = year1Total > NON_CONC_CAP;
        const band = calculateBringForwardRule(
            bringForwardData.totalSuperBalance,
            isTriggered ? year1Total : NON_CONC_CAP
        );
        const maxAllowed = isTriggered ? band.maximumContribution : NON_CONC_CAP;
        const windowYears = isTriggered ? Math.max(0, band.yearsToUse || 0) : 1;
        const windowTotal = [year1Total, year2Total, year3Total]
            .slice(0, windowYears)
            .reduce((sum, value) => sum + value, 0);

        // Validate against limits
        if (windowTotal > maxAllowed && isTriggered) {
            errors.push({
                type: 'limit',
                message: `Contributions in the ${windowYears}-year bring-forward window ($${windowTotal.toLocaleString()}) exceed the TSB-based limit ($${maxAllowed.toLocaleString()})`,
                value: windowTotal - maxAllowed
            });
        }

        // Year-by-year validation
        const yearValidation = [
            {
                year: bringForwardData.triggerYear,
                actual: bringForwardData.year1Contribution,
                planned: bringForwardData.plannedYear1,
                total: year1Total,
                status: year1Total <= maxAllowed ? 'valid' : 'exceeded',
                remaining: maxAllowed - year1Total
            },
            {
                year: bringForwardData.triggerYear + 1,
                actual: bringForwardData.year2Contribution,
                planned: bringForwardData.plannedYear2,
                total: year2Total,
                cumulativeTotal: year1Total + year2Total,
                remaining: maxAllowed - (year1Total + year2Total)
            },
            {
                year: bringForwardData.triggerYear + 2,
                actual: bringForwardData.year3Contribution,
                planned: bringForwardData.plannedYear3,
                total: year3Total,
                cumulativeTotal: threeYearTotal,
                remaining: maxAllowed - threeYearTotal
            }
        ];

        // Timeline and expiry
        const currentYear = new Date().getFullYear();
        const yearsIntoWindow = currentYear - bringForwardData.triggerYear;
        const yearsRemaining = Math.max(0, windowYears - yearsIntoWindow);
        const expired = isTriggered && yearsIntoWindow >= windowYears;

        if (expired) {
            warnings.push({
                type: 'expiry',
                message: 'Bring-forward window has expired - new window can start',
                severity: 'info'
            });
        } else if (yearsRemaining === 1) {
            warnings.push({
                type: 'expiry',
                message: 'Final year of bring-forward window - use it or lose it',
                severity: 'warning'
            });
        }

        // Optimization suggestions
        const suggestions = generateOptimizationSuggestions(
            windowTotal,
            maxAllowed,
            yearValidation,
            bringForwardData.age
        );

        setResults({
            eligible: errors.length === 0,
            errors,
            warnings,
            triggered: isTriggered,
            maxAllowed,
            windowYears,
            yearValidation,
            threeYearTotal,
            windowTotal,
            remaining: Math.max(0, maxAllowed - windowTotal),
            utilizationPercent: maxAllowed > 0 ? (windowTotal / maxAllowed) * 100 : 0,
            yearsRemaining,
            expired,
            suggestions
        });
        setActiveTab('results');
    };

    const generateOptimizationSuggestions = (total, max, yearData, age) => {
        const suggestions = [];
        const remaining = max - total;

        if (remaining > 50000 && age < 70) {
            suggestions.push({
                title: 'Unused Capacity',
                message: `You have $${remaining.toLocaleString()} remaining in your 3-year window`,
                action: 'Consider maximizing this opportunity - it may not be available in future',
                priority: 'high'
            });
        }

        if (total > max * 0.9 && total <= max) {
            suggestions.push({
                title: 'Near Limit',
                message: 'You\'ve used over 90% of your bring-forward capacity',
                action: 'Monitor carefully to avoid exceeding the limit',
                priority: 'medium'
            });
        }

        // Age-based suggestions
        if (age >= 70 && age < 75) {
            suggestions.push({
                title: 'Age Consideration',
                message: 'Bring-forward not available after age 75',
                action: 'This may be your last opportunity to use bring-forward',
                priority: 'high'
            });
        }

        // Timing suggestion
        const year3Planned = yearData[2]?.planned || 0;
        if (year3Planned > 0 && yearData[2]?.remaining < 0) {
            suggestions.push({
                title: 'Adjust Year 3 Contribution',
                message: 'Your planned Year 3 contribution exceeds remaining capacity',
                action: `Reduce Year 3 to $${(year3Planned + yearData[2].remaining).toLocaleString()}`,
                priority: 'high'
            });
        }

        return suggestions;
    };

    useEffect(() => {
        calculateBringForward();
    }, [bringForwardData]);

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* Eligibility Check */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-semibold text-blue-900 mb-2">📋 Bring-Forward Rule Basics</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Allows up to ${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()} in non-concessional contributions over 3 years</li>
                    <li>• Triggered when you exceed annual cap (${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}) in any year</li>
                    <li>• Must be under age 75 when contribution made</li>
                    <li>• TSB must be below ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M (no non-concessional contributions at or above this)</li>
                    <li>• Cannot trigger new bring-forward while existing one is active</li>
                </ul>
            </div>

            {/* Personal Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">Your Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Super Balance (at start of FY)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={bringForwardData.totalSuperBalance}
                            onChange={(e) => setBringForwardData({ ...bringForwardData, totalSuperBalance: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Your Age
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={bringForwardData.age}
                            onChange={(e) => setBringForwardData({ ...bringForwardData, age: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Trigger Year (when you exceeded $120K)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={bringForwardData.triggerYear}
                            onChange={(e) => setBringForwardData({ ...bringForwardData, triggerYear: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* 3-Year Contributions */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">3-Year Contribution Plan</h3>
                <div className="space-y-4">
                    {['Year 1', 'Year 2', 'Year 3'].map((label, index) => (
                        <div key={index} className="border rounded-lg p-4">
                            <h4 className="font-medium mb-3">{label} (FY {bringForwardData.triggerYear + index}-{bringForwardData.triggerYear + index + 1})</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Already Contributed
                                    </label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={index === 0 ? bringForwardData.year1Contribution : 
                                               index === 1 ? bringForwardData.year2Contribution : 
                                               bringForwardData.year3Contribution}
                                        onChange={(e) => {
                                            const field = index === 0 ? 'year1Contribution' : 
                                                        index === 1 ? 'year2Contribution' : 'year3Contribution';
                                            setBringForwardData({ ...bringForwardData, [field]: Number(e.target.value) });
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Planning to Contribute
                                    </label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={index === 0 ? bringForwardData.plannedYear1 : 
                                               index === 1 ? bringForwardData.plannedYear2 : 
                                               bringForwardData.plannedYear3}
                                        onChange={(e) => {
                                            const field = index === 0 ? 'plannedYear1' : 
                                                        index === 1 ? 'plannedYear2' : 'plannedYear3';
                                            setBringForwardData({ ...bringForwardData, [field]: Number(e.target.value) });
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <button
                type="button"
                onClick={calculateBringForward}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
            >
                Calculate Bring-Forward
            </button>
        </div>
    );

    const renderResults = () => {
        if (!results) return null;

        return (
            <div className="space-y-6">
                {/* Eligibility Status */}
                {results.eligible ? (
                    <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-green-900 text-lg mb-2">✅ Bring-Forward Rule Available</h3>
                        <p className="text-green-800">
                            You can contribute up to ${results.maxAllowed.toLocaleString()} over 3 years
                        </p>
                        {!results.expired && results.yearsRemaining > 0 && (
                            <p className="text-green-700 mt-2">
                                {results.yearsRemaining} year(s) remaining in your current window
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                        <h3 className="font-semibold text-red-900 text-lg mb-2">❌ Not Eligible</h3>
                        {results.errors.map((error, i) => (
                            <p key={i} className="text-red-800">• {error.message}</p>
                        ))}
                    </div>
                )}

                {/* Summary Cards */}
                {results.eligible && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                                <div className="text-sm text-gray-600 mb-1">3-Year Total</div>
                                <div className="text-2xl font-bold text-blue-600">
                                    ${results.threeYearTotal.toLocaleString()}
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                                <div className="text-sm text-gray-600 mb-1">Remaining Capacity</div>
                                <div className="text-2xl font-bold text-green-600">
                                    ${results.remaining.toLocaleString()}
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                                <div className="text-sm text-gray-600 mb-1">Utilization</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {results.utilizationPercent.toFixed(1)}%
                                </div>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
                                <div className="text-sm text-gray-600 mb-1">Years Remaining</div>
                                <div className="text-2xl font-bold text-yellow-600">
                                    {results.yearsRemaining}
                                </div>
                            </div>
                        </div>

                        {/* Year-by-Year Breakdown */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="font-semibold text-lg mb-4">Year-by-Year Breakdown</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-2">Financial Year</th>
                                            <th className="text-right py-2">Contributed</th>
                                            <th className="text-right py-2">Planned</th>
                                            <th className="text-right py-2">Total</th>
                                            <th className="text-right py-2">Cumulative</th>
                                            <th className="text-right py-2">Remaining</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.yearValidation.map((year, index) => (
                                            <tr key={index} className="border-b hover:bg-gray-50">
                                                <td className="py-3 font-medium">
                                                    FY {year.year}-{year.year + 1}
                                                </td>
                                                <td className="text-right py-3">
                                                    ${year.actual.toLocaleString()}
                                                </td>
                                                <td className="text-right py-3">
                                                    ${year.planned.toLocaleString()}
                                                </td>
                                                <td className="text-right py-3 font-semibold">
                                                    ${year.total.toLocaleString()}
                                                </td>
                                                <td className="text-right py-3">
                                                    ${(year.cumulativeTotal || year.total).toLocaleString()}
                                                </td>
                                                <td className={`text-right py-3 font-semibold ${
                                                    year.remaining < 0 ? 'text-red-600' : 'text-green-600'
                                                }`}>
                                                    ${year.remaining.toLocaleString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h4 className="font-semibold mb-4">Capacity Utilization</h4>
                            <div className="relative pt-1">
                                <div className="flex mb-2 items-center justify-between">
                                    <div className="text-xs text-gray-600">$0</div>
                                    <div className="text-xs font-medium text-gray-700">
                                        ${results.threeYearTotal.toLocaleString()} / ${results.maxAllowed.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-600">${results.maxAllowed.toLocaleString()}</div>
                                </div>
                                <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-gray-200">
                                    <div 
                                        className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                            results.utilizationPercent > 100 ? 'bg-red-500' :
                                            results.utilizationPercent > 90 ? 'bg-yellow-500' :
                                            'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(results.utilizationPercent, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Optimization Suggestions */}
                        {results.suggestions.length > 0 && (
                            <div className="bg-white p-6 rounded-lg shadow">
                                <h3 className="font-semibold text-lg mb-4">💡 Optimization Suggestions</h3>
                                <div className="space-y-4">
                                    {results.suggestions.map((suggestion, index) => (
                                        <div 
                                            key={index}
                                            className={`border-l-4 p-4 rounded ${
                                                suggestion.priority === 'high' ? 'bg-red-50 border-red-500' :
                                                'bg-yellow-50 border-yellow-500'
                                            }`}
                                        >
                                            <h4 className="font-semibold mb-1">{suggestion.title}</h4>
                                            <p className="text-sm text-gray-700 mb-2">{suggestion.message}</p>
                                            <p className="text-sm font-medium text-gray-900">→ {suggestion.action}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Warnings */}
                {results.warnings.length > 0 && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes</h4>
                        <ul className="space-y-1">
                            {results.warnings.map((warning, index) => (
                                <li key={index} className="text-sm text-yellow-800">• {warning.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <CalculationTransparency
                    kind="smsf"
                    title="Bring-Forward Transparency"
                    inputs={{
                        currentAge: bringForwardData.age,
                        totalBalance: bringForwardData.totalSuperBalance,
                        contributionAmount: (Number(bringForwardData.year1Contribution) || 0) + (Number(bringForwardData.year2Contribution) || 0) + (Number(bringForwardData.year3Contribution) || 0),
                    }}
                    results={{
                        ...results,
                        tableData: results.yearValidation || [],
                    }}
                />
            </div>
        );
    };

    const renderEducation = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📚 Understanding Bring-Forward Rule</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">What is it?</h4>
                        <p className="text-gray-700">
                            The bring-forward rule allows you to make up to 3 years' worth of non-concessional contributions 
                            (${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()}) in a single year or spread over 3 years. It's triggered automatically when you contribute 
                            more than the annual cap (${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}) in any financial year.
                        </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Example Scenario</h4>
                        <div className="text-green-800 space-y-2 text-sm">
                            <p><strong>Sarah (age 60) receives an inheritance:</strong></p>
                            <ul className="list-disc list-inside ml-4">
                                <li>Year 1: Contributes $250,000 (triggers bring-forward)</li>
                                <li>Year 2: Contributes $100,000</li>
                                <li>Year 3: Contributes $40,000</li>
                                <li><strong>Total: ${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()} over 3 years ✓</strong></li>
                            </ul>
                            <p className="mt-2 font-semibold">Sarah maximized her non-concessional contributions efficiently!</p>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Key Rules</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                            <li>Automatically triggered when you exceed ${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()} in a year</li>
                            <li>Once triggered, you have 3 years to use up to ${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()}</li>
                            <li>Cannot start new bring-forward while one is active</li>
                            <li>Unused amounts don't carry forward (use within 3 years)</li>
                            <li>Age must be under 75 when contribution made</li>
                            <li>TSB must be below ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M at start of financial year for full access</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Considerations</h4>
                        <ul className="list-disc list-inside space-y-1 text-yellow-800 text-sm">
                            <li>Once triggered, you're locked into the 3-year window</li>
                            <li>If you turn 75 during the window, you can't contribute in remaining years</li>
                            <li>Exceeding ${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()} triggers excess contributions tax (47%)</li>
                            <li>Different TSB thresholds may apply partial bring-forward</li>
                            <li>Downsizer contributions ($300K) are separate from bring-forward</li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">TSB Threshold Interactions</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2">TSB on 30 June</th>
                                        <th className="text-right py-2">Bring-Forward Available</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2">Less than ${(FinancialConfig.TSB_THRESHOLDS.BRING_FORWARD_TRIGGER / 1e6).toFixed(2)}M</td>
                                        <td className="text-right font-semibold text-green-600">Full ${BRING_FORWARD.MAXIMUM_CONTRIBUTION.toLocaleString()}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">${(FinancialConfig.TSB_THRESHOLDS.BRING_FORWARD_TRIGGER / 1e6).toFixed(2)}M - ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M</td>
                                        <td className="text-right font-semibold text-yellow-600">Partial (1–2 years)</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2">${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M or more</td>
                                        <td className="text-right font-semibold text-red-600">$0 (No contributions)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Bring-Forward Rule Calculator</h2>
                <p className="text-gray-600">
                    Track and optimize your 3-year non-concessional contribution window
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'calculator', label: 'Calculator', icon: '🧮' },
                        { id: 'results', label: 'Results', icon: '📊' },
                        { id: 'education', label: 'How It Works', icon: '📚' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'calculator' && renderCalculator()}
                {activeTab === 'results' && renderResults()}
                {activeTab === 'education' && renderEducation()}
            </div>
        </div>
    );
};

SMSFBringForwardCalculator.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFBringForwardCalculator;

